import { Link } from '@mui/icons-material';
import { EdgeHandle } from '../common/EdgeHandle.tsx';
import { Position, ReactFlowInstance, Node } from 'reactflow';
import FormNode, { FormNodeProps } from './FormNode.tsx';
import { useStore } from '../../../../store/store.ts';
import { useMemo } from 'react';
import { Typography } from '@mui/material';
import { EmbedWorkflowConfiguration, WorkflowNode } from '../../../../api/model.ts';

const FormProxyNode = ({ id, type, data, ...rest }: FormNodeProps) => {
  const reactFlowInstance: ReactFlowInstance | null = useStore().mainReactFlowInstance;
  const formReactFlowInstance: ReactFlowInstance | null = useStore().formReactFlowInstance;
  const selectedEmbedWorkflows = useStore().selectedEmbedWorkflows;

  const proxiedNode = useMemo(() => {
    if (!reactFlowInstance || !selectedEmbedWorkflows) return undefined;

    if (data.target) {
      const edge = reactFlowInstance.getEdges().find((edge) => edge.source === id);
      const targetEmbedWorkflowNode: Node<WorkflowNode> | undefined = edge
        ? reactFlowInstance.getNode(edge.target)
        : undefined;
      const targetConfig = targetEmbedWorkflowNode?.data?.configuration as EmbedWorkflowConfiguration;
      const embedWorkflowId = targetConfig?.workflow_id;
      const embedWorkflow = selectedEmbedWorkflows.find(
        (w) => w.id === embedWorkflowId && w.revision === targetConfig.revision,
      );

      const proxyTargetNodeId = targetConfig?.input_node_mappings?.find((m) => m.parent_node_id === id)?.child_node_id;

      if (proxyTargetNodeId && embedWorkflow) {
        return embedWorkflow.nodes.find((n) => n.id === proxyTargetNodeId);
      }
    }

    if (data.source) {
      const edge = reactFlowInstance.getEdges().find((edge) => edge.target === id);
      const sourceEmbedWorkflowNode: Node<WorkflowNode> | undefined = edge
        ? reactFlowInstance.getNode(edge.source)
        : undefined;
      const sourceConfig = sourceEmbedWorkflowNode?.data?.configuration as EmbedWorkflowConfiguration;
      const embedWorkflowId = sourceConfig?.workflow_id;
      const embedWorkflow = selectedEmbedWorkflows.find(
        (w) => w.id === embedWorkflowId && w.revision === sourceConfig.revision,
      );

      const proxySourceNodeId = sourceConfig?.output_node_mappings?.find((m) => m.parent_node_id === id)?.child_node_id;

      if (proxySourceNodeId && embedWorkflow) {
        return embedWorkflow.nodes.find((n) => n.id === proxySourceNodeId);
      }
    }

    return undefined;
  }, [reactFlowInstance, id, type, data, formReactFlowInstance, selectedEmbedWorkflows]);

  if (proxiedNode) {
    return (
      <FormNode
        id={id}
        data={{
          ...data,
          name: `embed workflow -> ${proxiedNode.name}`,
        }}
        type={proxiedNode.type}
        {...rest}
      />
    );
  }

  return (
    <div className="flex justify-center items-center">
      <Link fontSize="small" />
      <div className="opacity-0">
        <EdgeHandle type="source" position={Position.Right} isConnectable={true} />
        <EdgeHandle type="target" position={Position.Left} isConnectable={true} />
      </div>
      <Typography variant="subtitle2" className="text-gray-400">
        Not Connected
      </Typography>
    </div>
  );
};

export default FormProxyNode;
